import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { getIsOverdue, getInCredit } from '@/features/payments/common/helpers';

interface DesktopPaymentTileProps {
  balanceDue: number | null | undefined;
  isLoading?: boolean;
  onPress: () => void;
  onMakePayment: () => void;
}

/**
 * DesktopPaymentTile - Payment summary tile for desktop home dashboard
 */
export const DesktopPaymentTile: React.FC<DesktopPaymentTileProps> = ({
  balanceDue,
  isLoading = false,
  onPress,
  onMakePayment,
}) => {
  const balance = Number(balanceDue || 0);
  const isOverdue = getIsOverdue(balance);
  const inCredit = getInCredit(balance);
  const hasBalance = balance > 0;
  const isZeroBalance = balance === 0;

  const getStatusText = (): string => {
    if (isOverdue) return 'Past due balance';
    if (inCredit) return 'Account credit';
    if (isZeroBalance) return 'No balance due';
    return 'Balance due';
  };

  const getBalanceDisplay = (): string => {
    if (inCredit) return `(${formatCurrency(Math.abs(balance))})`;
    return formatCurrency(balance);
  };

  const getBalanceColor = () => {
    if (isOverdue) return styles.balanceOverdue;
    if (inCredit) return styles.balanceCredit;
    return styles.balanceNormal;
  };

  const getIconColor = () => {
    if (isOverdue) return PRIMITIVE_COLORS.resioDangerRed;
    if (inCredit || isZeroBalance) return PRIMITIVE_COLORS.successGreen;
    return PRIMITIVE_COLORS.resioBlue;
  };

  return (
    <TouchableOpacity style={styles.tile} onPress={onPress} activeOpacity={0.8}>
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <RESIDENTS_SVG_COMPONENTS.CREDIT_CARD width={24} height={24} color={getIconColor()} />
        </View>
        <Text style={styles.title}>Payments</Text>
      </View>

      <View style={styles.content}>
        {isLoading ? (
          <ActivityIndicator size="small" color={SEMANTIC_COLORS.text.tertiary} />
        ) : (
          <>
            <Text style={[styles.balance, getBalanceColor()]}>{getBalanceDisplay()}</Text>
            <Text style={styles.status}>{getStatusText()}</Text>
          </>
        )}
      </View>

      {hasBalance && !isLoading && (
        <TouchableOpacity
          style={[styles.actionButton, isOverdue && styles.actionButtonOverdue]}
          onPress={(e) => {
            e.stopPropagation();
            onMakePayment();
          }}
          activeOpacity={0.8}
        >
          <Text style={[styles.actionButtonText, isOverdue && styles.actionButtonTextOverdue]}>
            Make a payment
          </Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  tile: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 20,
    flex: 1,
    minHeight: 160,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: PRIMITIVE_COLORS.white10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  content: {
    flex: 1,
  },
  balance: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 4,
  },
  balanceNormal: {
    color: SEMANTIC_COLORS.text.primary,
  },
  balanceOverdue: {
    color: PRIMITIVE_COLORS.resioDangerRed,
  },
  balanceCredit: {
    color: PRIMITIVE_COLORS.successGreen,
  },
  status: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.tertiary,
  },
  actionButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  actionButtonOverdue: {
    backgroundColor: PRIMITIVE_COLORS.resioPink,
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  actionButtonTextOverdue: {
    color: PRIMITIVE_COLORS.gray950,
  },
});

export default DesktopPaymentTile;
